import { useEffect, useRef, useState, type MouseEvent, type RefObject } from "react"
import { MoreVert } from "@mui/icons-material"
import { Box, IconButton, Tab, Tooltip, type SxProps, type Theme } from "@mui/material"

// Fills the tab (capped by its maxWidth) so the name shrinks instead of clipping the ⋮ button.
const TAB_LABEL_ROW_SX = { display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, width: "100%" } satisfies SxProps<Theme>

// minWidth:0 + flex lets the name shrink below content width so the ellipsis engages.
const TAB_NAME_SX = { flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } satisfies SxProps<Theme>

// Keeps the ⋮ at full size beside the shrinking name.
const ACTIONS_BUTTON_SX = { flexShrink: 0, ml: 0.25, p: 0.25, color: "inherit" } satisfies SxProps<Theme>

// minWidth:0/maxWidth override the theme's 160px MuiTab so a tab hugs its label (capped, so long
// names ellipse); selected-driven fontWeight stays inline.
const TAB_BASE_SX = {
  textTransform: "none",
  letterSpacing: "0.02em",
  fontSize: "1.125rem",
  // True black on light / white on dark; selection is carried by the indicator bar + weight.
  color: "common.onBackground",
  // Idle tabs rest dimmed (opacity per-tab below), lift to full opacity on hover.
  transition: "opacity 0.2s ease",
  "&:hover": { opacity: 1 },
  // Fill the strip height, drop MUI's 48px floor; small py keeps the label off the 50px header edge.
  height: "100%",
  minHeight: 0,
  py: 0.25,
  px: 0.75,
  minWidth: 0,
  maxWidth: 200,
  "&.MuiTab-root": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
} satisfies SxProps<Theme>

// Whether `element`'s text is ellipsis-clipped, via ResizeObserver (overflow can't be read during
// render). Gates the name tooltip so it shows only when the label is actually truncated.
function useIsTruncated(): [RefObject<HTMLSpanElement | null>, boolean] {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  useEffect(() => {
    const element = ref.current
    // Absent under jsdom (no layout to measure) — skip, leaving the tooltip disabled (correct default).
    if (!element || typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(() => {
      setIsTruncated(element.scrollWidth > element.clientWidth)
    })
    observer.observe(element)
    return () => { observer.disconnect() }
  }, [])
  return [ref, isTruncated]
}

export interface DashboardTabProps {
  // The instance slug — both the MUI Tabs match value and the URL form the parent navigates to.
  value: string
  name: string
  // onSelect carries the chosen slug (not a DOM event), so the parent can navigate without re-deriving it.
  onSelect: (slug: string) => void
  // Opens the active dashboard's actions menu, anchored to the ⋮ (only the selected tab renders it).
  onActions?: (anchorEl: HTMLElement) => void
  // Injected by MUI <Tabs> via cloneElement; forwarded to <Tab> so selection styling, the sliding
  // indicator and roving tabindex keep working. `selected` also gates the ⋮.
  selected?: boolean
}

// component="div" (not the default <button>) so the active tab can nest the ⋮ IconButton — an
// interactive control inside a <button> is invalid HTML; we drive selected/tabIndex/aria ourselves.
export default function DashboardTab({ value, name, onSelect, onActions, selected }: DashboardTabProps) {
  const [nameRef, isNameTruncated] = useIsTruncated()

  const handleActions = (event: MouseEvent<HTMLButtonElement>) => {
    // Keep the click off the tab: don't select/navigate, just open the menu anchored to the button.
    event.stopPropagation()
    onActions?.(event.currentTarget)
  }

  return (
    <Tab
      component="div"
      role="tab"
      aria-selected={selected ?? false}
      tabIndex={selected ? 0 : -1}
      value={value}
      onClick={() => { onSelect(value) }}
      label={
        <Box sx={TAB_LABEL_ROW_SX}>
          {/* Tooltip only when the name is clipped — a tooltip echoing a fully-visible label is noise. */}
          <Tooltip title={name} disableHoverListener={!isNameTruncated}>
            <Box component="span" ref={nameRef} sx={TAB_NAME_SX}>
              {name}
            </Box>
          </Tooltip>
          {selected && onActions && (
            <Tooltip title="Dashboard actions">
              <IconButton
                aria-label={`Actions for ${name}`}
                aria-haspopup="menu"
                size="small"
                sx={ACTIONS_BUTTON_SX}
                onMouseDown={(event) => { event.stopPropagation() }}
                onClick={handleActions}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      }
      // Selected-dependent rest weight + opacity; the static base (incl. the hover lift) is TAB_BASE_SX.
      sx={{
        ...TAB_BASE_SX,
        fontWeight: selected ? 600 : 500,
        opacity: selected ? 1 : 0.6,
      }}
    />
  )
}
